// Third Party
import * as React from 'react';
import Image from 'next/image';
import { StaticImageData } from 'next/image';
import { useMergeRefs } from 'use-callback-ref';

// Utils
import { WithCommonProps } from '@types';
import { useAnalytics } from '@contexts/use-analytics';

// UI
import GoogleLogo from '../../../public/assets/svg/social-logos/google.svg';
import FacebookLogo from '../../../public/assets/svg/social-logos/facebook.png';
import AppleLogo from '../../../public/assets/svg/social-logos/apple.svg';
import { Button, ButtonProps } from '@components/button';

export enum SocialLoginType {
  Google = 'Google',
  Facebook = 'Facebook',
  Apple = 'Apple',
}

const socialLogins: {
  value: SocialLoginType;
  logo: StaticImageData;
  text: string;
}[] = [
  {
    value: SocialLoginType.Google,
    logo: GoogleLogo,
    text: 'Continue with Google',
  },
  {
    value: SocialLoginType.Facebook,
    logo: FacebookLogo,
    text: 'Continue with Facebook',
  },
  {
    value: SocialLoginType.Apple,
    logo: AppleLogo,
    text: 'Continue with Apple',
  },
];

export interface SocialLoginProps extends WithCommonProps<ButtonProps> {
  /** Name of the login to be displayed */
  social: SocialLoginType;
  /** Whether this is being used in a signup context (defaults to true) */
  isSignUp?: boolean;
}

export const SocialLogin = React.forwardRef(
  (
    props: SocialLoginProps,
    ref: React.ForwardedRef<HTMLButtonElement>
  ): JSX.Element => {
    const { children, social, isSignUp = true, onClick, ...rest } = props;
    const newRef = React.useRef<HTMLButtonElement>(null);
    const buttonRef = useMergeRefs<HTMLButtonElement>([newRef, ref]);
    const { emitSignUpBeginEvent } = useAnalytics();

    // Find the social login entry that matches the socialType prop
    const socialLoginEntry = socialLogins.find(
      (entry) => entry.value === social
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isSignUp) {
        emitSignUpBeginEvent(social.toLowerCase());
      }
      onClick?.(event);
    };

    if (!socialLoginEntry) {
      return <div />;
    } else {
      return (
        <>
          <Button
            variant="outlined"
            color="primary"
            {...rest}
            onClick={handleClick}
            ref={buttonRef}
            aria-describedby={socialLoginEntry.text}
            className="w-full bg-white hover:bg-off-white active:scale-95 active:bg-gray-xlight"
          >
            <div className="flex w-full items-center justify-between py-1">
              <div className="w-[20px]">
                <Image
                  width={20}
                  height={20}
                  layout="responsive"
                  src={socialLoginEntry.logo}
                  className="object-cover object-center"
                  alt={'Savvy ' + social + ' login'}
                />
              </div>
              <p>{socialLoginEntry.text}</p>
              <div className="w-[20px]" />
            </div>
          </Button>
        </>
      );
    }
  }
);

SocialLogin.displayName = 'SocialLogin';
